import * as mediasoup from 'mediasoup-client';
import { LottieMarkerTypes } from './lottie/lottie-defines';
import { DynamicItemType, IDynamicIdentifier, IStagePosition } from 'lottie-json-helper/lib/types';
import { IScene } from './project/scene-model';
import { VideoEditTake } from './job/edit-job-schema';
import { ILocalAsset } from './assets/assets.model';

export type SingleOrArray<T> = T | T[];
export function getAnArray<T>(input: T | T[]): T[] {
    return Array.isArray(input) ? input : [input];
}
export interface IPaginationQuery {
    page?: string;
    limit?: string;
    search?: string;
}
export interface IPagainationNumber {
    page: number;
    limit: number;
}
export enum MAT_ICON {
    DOWNLOAD = 'download',
    GRAPHIC_EQ = 'graphic_eq',
    COPY = 'content_copy',
    CODE = 'code-icon',
}
/** Generic tri-state decision for a feature/setting: always on, always off, or let the AI decide. */
export enum DecisionModeEnum {
    YES = 'yes',
    NO = 'no',
    LET_AI_DECIDE = 'let_ai_decide',
}
export enum InviteStatusEnum {
    PENDING = 'pending',
    ACCEPTED = 'accepted',
    DECLINED = 'declined',
}
export enum CreativeStatusEnum {
    STRUCTURE = 'STRUCTURE',
    DONE = 'DONE',
    GENERATING_IMAGES = 'GENERATING_IMAGES',
}

export enum TranscriptStatusEnum {
    NONE = 'NONE',
    GENERATING_SUBTITLES = 'GENERATING_SUBTITLES',
    DONE = 'DONE',
}

export interface ICreative {
    prompt: string;
    videoTagId: string;
    status?: CreativeStatusEnum;
}

export type KeysMatching<T, V> = {
    [K in keyof T]: T[K] extends V ? K : never;
}[keyof T];

/**
 * Never change the ORDER of the enums!
 */
export enum ProjectStatusEnum {
    PLANNING = 'planning',
    RECORDING = 'recording',
    EDITING = 'editing',
    EXPORTING = 'exporting',
    DONE = 'done',
}

export enum ProjectStatusTabsEnum {
    PLANNING = 'planning',
    RECORDING = 'recording',
    EDITING = 'editing',
    EXPORTING = 'exporting',
}

export enum Button_USE_CASE {
    DOWNLOAD = 'download',
    COPY = 'copy',
    CODE = 'code',
}

export enum ROLE {
    HOST = 'Host',
    AUDIENCE = 'Audience',
    PARTICIPANT = 'Participant',
    VIDEO = 'Video',
    RTMP = 'Rtmp',
    SERVER = 'Server',
}

export enum CONNECT_VIDEO_STATUS {
    Null = 'null',
    Requested = 'requested',
    Connected = 'connected',
}

export interface SerializedPeer {
    id: string;
    displayName: string;
    role: ROLE;
    picture?: string;
    userId?: string;
}

export interface StageOptions {
    stageCamEnabled: boolean;
    stageMicEnabled: boolean;
    softPosition: number;
    hardPosition: number;
    streamId?: string;
}

export interface IPathAndDashAndAutoplay {
    videoSource: IPathAndDash;
    autoPlay: boolean;
}

export class SflPeer {
    public id: string;
    public displayName: string;
    public role = ROLE.AUDIENCE;
    public platform: string;
    public picture: string;
    public connectVideoStatus? = CONNECT_VIDEO_STATUS.Null;
    public camStream?: SflMedia;
    public screenStream?: SflMedia;
    public mediaStream?: SflMedia;
    public videoAndAutoplay?: IPathAndDashAndAutoplay = {
        videoSource: { uploadPath: '', dashPath: '' },
        autoPlay: false,
    };
    public userId?: string;
    public userShuffllToken?: string;
    public participationToken?: string; // When asked to participate we can get a token from the host to aprove automatically
    public approvedByHost?: boolean;

    constructor(
        id: string = null,
        displayName: string = null,
        role: ROLE = null,
        platform: string = null,
        picture: string = null
    ) {
        this.id = id;
        this.displayName = displayName;
        this.role = role;
        this.platform = platform;
        this.picture = picture;
    }

    getSerializedPeer(): SerializedPeer {
        return {
            id: this.id,
            displayName: this.displayName,
            role: this.role,
            picture: this.picture,
            userId: this.userId,
        };
    }
}

export class MediaPositions {
    public id: string; //
    public mediaSource: SflMediaSource;
    public stageOptions: StageOptions = {
        stageCamEnabled: true,
        stageMicEnabled: true,
        softPosition: null,
        hardPosition: null,
    };

    getPosition() {
        const calculatedPosition = this.stageOptions.hardPosition ?? this.stageOptions.softPosition;
        return calculatedPosition + 1; // So it will be readable
    }

    createStageOptionsNotification(): StageOptions {
        return { ...this.stageOptions, streamId: this.id };
    }

    public mixStrings(string1: string, string2: string): string {
        const minLength = Math.min(string1.length, string2.length);
        let mixedString = '';

        for (let i = 0; i < minLength; i++) {
            mixedString += string1.charAt(i) + string2.charAt(i);
        }

        if (string1.length > string2.length) {
            mixedString += string1.slice(minLength);
        } else if (string2.length > string1.length) {
            mixedString += string2.slice(minLength);
        }

        return mixedString;
    }
}

export class SflPeerAndSource extends MediaPositions {
    public peer: SflPeer;

    constructor(peer: SflPeer, mediaSource: SflMediaSource) {
        super();
        this.peer = peer;
        this.mediaSource = mediaSource;
        this.id = this.mixStrings(peer.id, mediaSource);
    }
}

export class SflVideo extends SflPeerAndSource {
    public canBePlayed: boolean = false;

    constructor(peer: SflPeer, mediaSource: SflMediaSource) {
        super(peer, mediaSource);
    }
}

export enum DirectorCommand {
    NEXT = 'NextBit',
    SKIP_BACK = 'SkipBackBit',
    RESTART_CURRENT = 'RestartCurrentBit',
    RESTART_SHOW = 'RestartShow',
    PAUSE = 'PauseBit',
    STOP_PAUSE = 'stop_pause', ///When paused pressed again, but we open a dialog to choose what to do next
    GO_TO_SPECIFIC_SCENE = 'specific-scene',
    CONTINUE = 'continue', ///Continue current take . not working right now
}

export enum VideoLayoutTypes {
    Custom = 'custom',
}

export interface Asset {
    name?: string;
    type?: 'svg' | 'png' | 'json';
    loops?: number;
    forBitId?: string;
}

export interface ILayout {
    lottiePath: string;
    _id: string;
    customLayoutId?: string;
    dynamicClientLayerId?: string;
}
export interface IExtendedLayout extends ILayout {
    // basePathForUpdateAssets: string;
    relativeLottiePath: string;
    baseDesignPath: string;
}
export class SflMessage {
    constructor(
        public id: string,
        public peer: SerializedPeer,
        public who: 'partner' | 'me',
        public message: string,
        public recvTime: Date,
        public sendStatus: string,
        public platform: 'Shuffll' | 'Youtube' | 'Twitch' = 'Shuffll'
    ) {}
}

export class SflFile {
    constructor(
        public name: string,
        public size: number,
        public blob: Blob
    ) {}
}

export class SflMedia extends MediaStream {
    public videoConsumer?: mediasoup.types.Consumer;
    public audioConsumer?: mediasoup.types.Consumer;
    public peer?: SflPeer;
    public source?: string;
    public volume?: number;
    public producerScore?: number[] = [];
    public consumerScore?: number[] = [];
    public scoreIndex?: string[] = [];
    public toggleSide = false;
    public size = 6;

    constructor();

    constructor(media: MediaStream);

    constructor(media?: MediaStream) {
        super();
    }
}

export enum RoomStatus {
    started = 'started',
    stopped = 'stopped',
}

export class Showroom {
    status: RoomStatus;
    startTime: number;
    stopTime: number;
    mutedAudio = false;
    mutedVideo = false;

    constructor() {}
}

export class SflRoomInfo {
    id: string;
    name: string;
    description;
    createTime;
    lastActiveTime;
    attendeePassword;
    speakerPassword;

    constructor() {}
}
export interface IFolder {
    id: string;
    name: string;
    assets: ILocalAsset[];
}

export interface IVideoAssetAndFlag {
    videoAsset: ILocalAsset;
    autoPlay: boolean;
}

export interface EmojiDialogData {
    animal: string;
    name: string;
}

export const VIDEORESOLUTION = [
    {
        width: 1920,
        height: 1080,
    },
    {
        width: 1280,
        height: 720,
    },

    {
        width: 640,
        height: 480,
    },
];

export const SIMULCASTENCODING: RTCRtpEncodingParameters[] = [
    { maxBitrate: 100000, rid: 'idk' },
    { maxBitrate: 300000, rid: 'idk' },
    { maxBitrate: 900000, rid: 'idk' },
];

export const SCREENSHARE_CONSTRAINTS = {
    video: {
        cursor: 'always',
    },
    audio: {
        echoCancellation: true,
        noiseSuppression: true,
        sampleRate: 44100,
    },
};

export enum RequestMethod {
    iCanPlayVideo = 'iCanPlayVideo',
    iCantPlayVideo = 'iCantPlayVideo',
    videoPlayable = 'videoPlayable',
    videoNotPlayable = 'videoNotPlayable',
    getRouterRtpCapabilities = 'getRouterRtpCapabilities',
    join = 'join',
    createWebRtcTransport = 'createWebRtcTransport',
    connectWebRtcTransport = 'connectWebRtcTransport',
    restartIce = 'restartIce',
    produce = 'produce',
    closePeerProducers = 'closePeerProducers',
    closeProducer = 'closeProducer',
    pauseProducer = 'pauseProducer',
    resumeProducer = 'resumeProducer',
    pauseConsumer = 'pauseConsumer',
    resumeConsumer = 'resumeConsumer',
    requestConsumerKeyFrame = 'requestConsumerKeyFrame',
    getProducerStats = 'getProducerStats',
    getConsumerStats = 'getConsumerStats',
    getTransportStats = 'getTransportStats',
    changeDisplayName = 'changeDisplayName',
    changePicture = 'changePicture',
    changeRole = 'changeRole',
    chatMessage = 'chatMessage',
    closePeer = 'closePeer',
    prompterUpdate = 'prompterUpdate',

    syncDocInfo = 'syncDocInfo',

    showStart = 'showStart',
    showStop = 'showStop',
    endScene = 'endScene',
    notifyTake2 = 'notifyTake2',

    roomInfo = 'roomInfo',
    changeLogo = 'changeLogo',
    announcementText = 'announcementText',
    videoFilter = 'videoFilter',

    connectVideo = 'connectVideo',
    connectApproval = 'connectApproval',
    disconnectVideo = 'disconnectVideo',

    switchComponent = 'switchComponent',

    muted = 'muted',
    unmuted = 'unmuted',

    askToParticipate = 'askToParticipate',
    respondToParticipationRequest = 'respondToParticipationRequest',

    invokeControlAction = 'invokeControlAction',
    invokeBit = 'invokeBit',
    invokeAction = 'invokeAction',
    invokePositionUpdate = 'invokePositionUpdate',
    invokePositionStageOptionUpdate = 'invokePositionStageOptionUpdate',
    dynamicValuesChanged = 'dynamicValuesChanged',
    transitionModeChanged = 'transitionModeChanged',
    newCompositionInTimeline = 'newCompositionInTimeline',

    goToScene = 'goToScene',
    bitStartedLoading = 'bit_start_loading',
    bitFinishedLoading = 'bit_finished_loading',
}

export enum TakeStartEnd {
    START = 'start',
    END = 'end',
}

export enum SflBoardComp {
    video = 'video',
    welcome = 'welcome',
    document = 'document',
    whiteboard = 'whiteboard',
    sharescreen = 'sharescreen',
    sharemedia = 'sharemedia',
}

export enum SflMediaSource {
    cameramic = 'cameramic',
    screen = 'screen',
    media = 'media',
}

export enum SflColor {
    primary = 'primary',
    secondary = 'secondary',
    tertiary = 'tertiary',
    success = 'success',
    warning = 'warning',
    danger = 'danger',
    light = 'light',
    medium = 'medium',
    dark = 'dark',
}

export class SflDocument {
    public id: number;
    public roomId: string;
    public uploadTime: string;
    public opened = false;

    constructor(public fileName: string) {}
}

export const makeRandomString = (length: number): string => {
    let outString = '';
    const inOptions = 'abcdefghijklmnopqrstuvwxyz0123456789';

    for (let i = 0; i < length; i++) {
        outString += inOptions.charAt(Math.floor(Math.random() * inOptions.length));
    }

    return outString;
};

export const getImageMeta = (url: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
        const img = document.createElement('img');
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
    });
};

export enum ActionControl {
    PLAY,
    PAUSE,
    RESTART,
    FORWARD5,
    BACKWARD5,
    SHOW,
    HIDE,
    SPIN,
    FILL,
    SETUP,
}

export enum StreamStatus {
    CREATED = 'created',
    STARTING = 'starting',
    ON_AIR = 'on_air',
    DRY_RUN = 'dry_run',
    PAUSED = 'paused',
    ERROR = 'error',
    SCHEDULED = 'scheduled',
    FINISHED = 'finished',
}

export enum Backgrounds {
    REGULAR = 'regular',
    SPARKS = 'sparks',
    DATING = 'dating',
}

export interface DynamicValuesChangeMessage {
    dynamicClientLayerId: string;
    dynamicValues: IDynamicText[] | IDynamicIdentifier[];
}

export interface PeerAndSourcePosition {
    peerAndSourceId: string;
    softPosition: number;
    hardPosition: number;
}

export interface PositionUpdate {
    positions: PeerAndSourcePosition[];
    // screenShares: string[];
    // media: string[];
}

export enum ExportStatus {
    READY = 'Ready',
    NOT_READY = 'Not Ready',
}

export interface IExportVideos {
    originalExported: IPathAndDash;
    exportedWithAudioClean: IPathAndDash;
}

export interface IPathAndDash {
    dashPath?: string;
    uploadPath?: string;
}

export interface ILottieJsonMarker {
    cm: string;
    dr: number;
    tm: number;
}

export interface ILottieMarker {
    startTimeInFrames: number;
    durationInFrames: number;
    type: LottieMarkerTypes;
}

export interface IStartLoopMarkers {
    startMark?: ILottieMarker;
    loopMark?: ILottieMarker;
}

export enum UploadType {
    FLV = 'flv',
    AVI = 'avi',
    MP4 = 'mp4',
    MKV = 'mkv',
    FUSEDMKV = 'fused.mkv',
    FUSEDMP4 = 'fused.mp4',
    FUSED_WITH_BACKGROUND_MUSIC_MP4 = 'fusedWithBGmusic.mp4',
    CLEANED_WITH_BACKGROUND_MUSIC_MP4 = 'cleanedWithBGmusic.mp4',
    CLEANED = 'clean.mp4',
    SEGMENTS = 'segments',
    THUMBNAILS = 'thumbnails',
}

export interface ICustomLayout extends Document {
    name: string;
    stagePositions?: IStagePositionsExtended[];
}

export interface IStagePositionsExtended extends IStagePosition {
    id: string;
}
export enum StagePositionDirectionsEnum {
    LEFT = 'left',
    LEFT_UP = 'left-up',
    LEFT_DOWN = 'left-down',
    RIGHT = 'right',
    RIGHT_UP = 'right-up',
    RIGHT_DOWN = 'right-down',
    CENTER = 'center',
    UP = 'up',
    DOWN = 'down',
    BACKGROUND = 'background',
}
export type StagePositionMatIcon =
    | 'south'
    | 'south_west'
    | 'south_east'
    | 'north'
    | 'north_east'
    | 'north_west'
    | 'west'
    | 'east'
    | 'keyboard_voice'
    | 'center_focus_strong';

export const DirectionToIconMap: Record<StagePositionDirectionsEnum, StagePositionMatIcon> = {
    [StagePositionDirectionsEnum.BACKGROUND]: 'keyboard_voice',
    [StagePositionDirectionsEnum.CENTER]: 'center_focus_strong',

    [StagePositionDirectionsEnum.LEFT]: 'west',
    [StagePositionDirectionsEnum.RIGHT]: 'east',

    [StagePositionDirectionsEnum.UP]: 'north',
    [StagePositionDirectionsEnum.DOWN]: 'south',

    [StagePositionDirectionsEnum.LEFT_UP]: 'north_west',
    [StagePositionDirectionsEnum.LEFT_DOWN]: 'south_west',

    [StagePositionDirectionsEnum.RIGHT_UP]: 'north_east',
    [StagePositionDirectionsEnum.RIGHT_DOWN]: 'south_east',
};

export enum StagePositionTypeEnum {
    MAIN = 'main',
    SECONDARY = 'secondary',
    BACKGROUND = 'background',
}
export interface IStagePositionConfigs {
    direction: StagePositionDirectionsEnum;
    matIcon: StagePositionMatIcon;
    title: string;
    index: number;
    type: StagePositionTypeEnum;
}
export interface IStagePositionWithConfigs extends IStagePositionsExtended {
    configs: IStagePositionConfigs;
}
export interface IVideoThumbnails {
    thumbnailsPath: string;
    numberOfThumbnails: boolean;
}

export enum MimeTypeEnum {
    ApplicationJson = 'application/json',
    ApplicationPdf = 'application/pdf',
    ApplicationXml = 'application/xml',
    ApplicationZip = 'application/zip',
    AudioMpeg = 'audio/mpeg',
    AudioOgg = 'audio/ogg',
    ImageGif = 'image/gif',
    ImageJpeg = 'image/jpeg',
    ImagePng = 'image/png',
    TextCss = 'text/css',
    TextCsv = 'text/csv',
    TextHtml = 'text/html',
    TextPlain = 'text/plain',
    VideoMp4 = 'video/mp4',
    VideoOgg = 'video/ogg',
    VideoWebm = 'video/webm',
    AudioWebm = 'audio/webm',
    AudioMp4 = 'audio/mp4',
    OCTECT_STREAM = 'application/octet-stream',
}

export enum VideoRecommend {
    FORCE = 'force',
    POSSIBLE = 'possible',
}

export interface DynamicAsset {
    id: string;
    path: string;
    content?: string;
    width: number;
    height: number;
    dynamics: IDynamicIdentifier[];
}

export interface IDynamicText {
    id?: string;
    newText?: string | Array<number>; //either text nor array of numbers for rgb
    layerObject?: any;
    ai?: boolean;
    ai_type?: DynamicItemType;
    ai_index?: number;
}

//Because when a participant enter, he search for dynamic values the host gave him, those values will be applied to every
//Bit because the names are the same (sfl_gradient0), then he will search to see if this existed in the dynamicValuesForHost
//He will see it does and will apply this change -> even though it is from other scene
export interface IHostDynamicObject extends IDynamicText {
    dynamicText: IDynamicText[];
    forScene?: string;
}

export interface IBackgroundMusic {
    _id?: string;
    folder?: string;
    displayName: string;
    filePath: string;
    mimetype?: string;
    duration?: number;
}

export enum AssetType {
    FOLDER = 'folder',
    IMAGE = 'image',
    VIDEO = 'video',
    Audio = 'audio',
    PDF = 'pdf',
}

export enum Mimetype {
    VIDEO_MP4 = 'video/mp4',
    VIDEO_WEBM = 'video/webm',
    VIDEO_OGG = 'video/ogg',
    GIF = 'image/gif',
    PNG = 'image/png',
    JPG = 'image/jpg',
    SVG = 'image/svg+xml',
    JPEG = 'image/jpeg',
}

export interface IPostMessageData {
    identifier: string;
    eventType?: string;
    args?: any;
}

export enum GoalType {
    General = 'General',
    Awareness = 'Awareness',
    Advocacy = 'Advocacy',
    Conversion = 'Conversion',
    Engagement = 'Engagement',
    Educate = 'Educate',
}

export interface VideoCue extends TextTrackCue {
    left?: any;
    right?: any;
    type?: string;
}

export interface PrompterMessage {
    type: string;
    from: string;
    payload: any;
}

export interface IPublication extends Document {
    publicationId: string;
    title: string;
    videos: { path: string; title?: string; thumbnail?: string }[];
    files?: { path: string; title: string }[];
}

export interface IOnBoardingQuestionConfigs {
    id: string;
    value?: string;
    hubspotPropertyId: string;
    index?: number;
}

export interface IOnboardingQuestion extends IOnBoardingQuestionConfigs {
    type: QuestionTypeEnum;
    options?: string[];
    innerLabel?: string;
    text?: string;
}

export interface IOnboardingStep {
    id?: string;
    stepTitle: string;
    stepSubTitle?: string;
    stepDescription?: string;
    topIcon?: string;

    questions: IOnboardingQuestion[];
    hideInUI?: boolean;
}

export enum QuestionTypeEnum {
    BADGE_SELECT = 'badge-select',
    SELECT = 'select',
    MULTI_SELECT = 'multi-select',
    TEXT = 'text',
    EMAIL = 'email',
    TEXT_AREA = 'text-area',
}

export interface IQuestion {
    id: string;
    type: QuestionTypeEnum;
    text?: string;
    innerLabel?: string;
    value?: string;
    options?: string[];
    defaultValue?: string;
}

export interface IStep {
    stepTitle: string;
    topIcon: string;
    questions: IQuestion[];
}

export interface IOnBoardingConfig {
    steps: IOnboardingStep[];
}

export interface IVideoTag {
    id: string;
    text: string;
    title: string;
}

export interface IToneOfVoice {
    id: string;
    description: string;
}

export interface IStorytellingTechnique {
    technique: string;
    description: string;
    whyItWorks: string;
    example: string;
}

export enum PromptTypeEnum {
    LINK = 'Link',
    GOOGLE_DOCS = 'Docs',
    LONG_SCRIPT = 'Script',
    SIMPLE_PROMPT = 'Prompt',
    GOOGLE_SLIDES = 'Slides',
}

export interface IVideoTagsConfig {
    tags: IVideoTag[];
}

export interface IToneOfVoiceConfig {
    tones: IToneOfVoice[];
}

export interface IStorytellingTechniqueConfig {
    techniques: IStorytellingTechnique[];
}

export interface ITagAndSuggestions {
    tag: IVideoTag;
    suggestions: string[];
}

export enum ExportQualityEnum {
    P1080 = 1080,
    P720 = 720,
    P480 = 480,
}

export interface ISceneAndEditTakes {
    scene: IScene;
    videoScenes: VideoEditTake[];
}

export interface ISceneAndEditTakesWithHasRecordedTake extends ISceneAndEditTakes {
    hasRecordedTakes: boolean;
}

/// Using fake detection interface because when triggering a false into false, the child component detect changes not being triggered .
export interface IFakeDetection {
    isPlaying: boolean;
    randomNumber: number;
}

export interface IFeedbackQuestion extends IFeedbackQuestionConfigs {
    id: string;
    type: QuestionTypeEnum;
    text?: string;
    options?: string[];
    value?: string[] | string;
}

export enum FeedbackEnum {
    GENERAL_FEEDBACK = 'General Feedback',
    EXPORT_FEEDBACK = 'Export Feedback',
    PLANNING_FEEDBACK = 'Planning Feedback',
}

export interface IFeedbackForm {
    id?: FeedbackEnum;
    feedbackTitle: string;
    feedbackDescription?: string;
    questions: IFeedbackQuestion[];
}

export interface IFeedbackQuestionConfigs {
    question_id: string;
    value?: string[] | string;
}

export interface IFeedbackConfigs {
    feedbackForms: IFeedbackForm[];
}

export enum ProjectRecordingTypeEnum {
    CAMERA = 'camera',
    MIC = 'mic',
    AI_VOICE = 'ai-voice',
    AVATAR = 'avatar',
}

export interface IProjectRecordingWithIndex {
    type: ProjectRecordingTypeEnum;
    index: number;
}

export enum CompositionTypeEnum {
    // ===== VIDEO COMPOSITIONS =====
    // Clean Video
    VID_CLEAN = 'vid_clean',
    VID_SHADOW = 'vid_shadow',
    SMART_VID = 'smart_vid',

    // Video with Logo
    VID_AND_LOGO_BUG = 'vid_and_logo_bug',
    VID_AND_LEFT_LOGO_BUG_STATIC = 'vid_and_left_logo_bug_static',
    VID_AND_RIGHT_LOGO_BUG_STATIC = 'vid_and_right_logo_bug_static',

    // Video with Text Overlays
    VID_AND_HEADLINE_TXT = 'vid_and_headline_txt',
    VID_AND_NAME_SUPER = 'vid_and_name_super',
    VID_AND_LOWER_BANNER_TXT = 'vid_and_lower_banner_txt',
    VID_AND_UPPER_BANNER_TXT = 'vid_and_upper_banner_txt',
    VID_AND_BULLETED_LIST = 'vid_and_bulleted-list',
    VID_RIGHT_BULLETED_LIST = 'vid_and_right_bulleted-list',

    // Picture-in-Picture (PIP)
    VID_AND_PIP_CENTER_LARGE = 'vid_pip_center_large',
    VID_AND_PIP_CENTER_AND_SUBTITLE_TXT = 'vid_pip_center_and_subtitle_txt',
    VID_PIP_TWO = 'vid_pip-2',
    VID_PIP_AND_LEFT_BULLETS_TXT = 'vid_pip_and_left_bullets_txt',
    VID_PIP_AND_RIGHT_BULLETS_TXT = 'vid_pip_and_right_bullets_txt',
    VID_PIP_SMALL_AND_LARGE = 'vid_pip_small_and_large',
    VID_PIP_INTERVIEW = 'vid_pip_interview',
    VID_PIP_AND_SUBTITLE_TXT = 'vid_pip_and_subtitle_txt',
    VID_MEDIA_PIP = 'vid_media_pip',

    // Side-by-Side Video
    VID_CLEAN_SIDE_BY_SIDE = 'vid_clean_side_by_side',
    VID_SIDE_BY_SIDE = 'vid_side-by-side',

    // Split Video
    VID_HORZ_SPLIT = 'vid_horz-split',
    VID_VERT_SPLIT = 'vid_vert-split',
    VID_HORZ_SPLIT_60_40 = 'vid_horz-split_60-40',
    VID_VERT_SPLIT_60_40 = 'vid_vert-split_60-40',
    VID_VERT_SPLIT_50_50_CENTER = 'vid_vert-split_50-50-center',
    VID_VERT_SPLIT_50_50_MID = 'vid_vert-split_50-50-mid',
    VID_VERT_SPLIT_50_50_TOP = 'vid_vert-split_50-50-top',

    // Dynamic Title Videos
    DYNAMIC_TITLE_VID_01 = 'dynamic-title_vid_01',
    DYNAMIC_TITLE_VID_02 = 'dynamic-title_vid_02',
    DYNAMIC_TITLE_VID_03 = 'dynamic-title_vid_03',
    DYNAMIC_TITLE_VID_04 = 'dynamic-title_vid_04',
    DYNAMIC_TITLE_VID_05 = 'dynamic-title_vid_05',

    // ===== IMAGE COMPOSITIONS =====
    // Single Images
    IMG_SINGLE = 'img_single',
    IMG_SINGLE_STATIC = 'img_single_static',
    IMG_SHADOW = 'img_shadow',
    IMG_AND_HEADLINE_TXT = 'img_and_headline_txt',

    // Image Slides
    IMG_SLIDES = 'img_slides',
    TWO_IMG_SLIDES = '2-img_slides',
    THREE_IMG_SLIDES = '3-img_slides',
    FOUR_IMG_SLIDES = '4-img_slides',

    // Split Images (Static)
    IMG_SPLIT_2_STATIC = 'img_split_2_static',
    IMG_SPLIT_3_STATIC = 'img_split_3_static',
    IMG_SPLIT_4_STATIC = 'img_split_4_static',

    // Image Frames with Text
    TWO_IMG_FRAMES_TXT = '2-img_frames_txt',
    THREE_IMG_FRAMES_TXT = '3-img_frames_txt',
    FOUR_IMG_FRAMES_TXT = '4-img_frames_txt',

    // ===== TEXT COMPOSITIONS =====
    // Headlines & Titles
    HEADLINE_TXT = 'headline_txt',
    HEADLINE_AND_SUBTITLE_TXT = 'headline_and_subtitle_txt',
    HEADLINE_1MIN_TXT = 'headline_1min_txt',

    // Subtitles & Numbers
    NUMBER_AND_SUBTITLE_TXT = 'number_and_subtitle_txt',
    SUBTITLE_AND_BULLETS_TXT = 'subtitle_and_bullets_txt',

    // Lists & Bullets
    BULLETED_LIST = 'bulleted-list',

    // Call-to-Action
    CTA_TXT = 'cta_txt',

    // Quotes & Reviews
    QUOTE_TXT = 'quote_txt',

    // Contact & Info
    CONTACT_INFO_TXT = 'contact_info_txt',

    // ===== SPECIAL COMPOSITIONS =====
    // Logos
    LOGO_INTRO = 'logo_intro',
    LOGO_END = 'logo_end',
    PRESENTED_BY = 'presented-by',

    // Ratings & Reviews
    PROPERTY_RATINGS = 'property_ratings',
    CUSTOMER_RATINGS = 'customer_ratings',
    CUSTOMER_REVIEW = 'customer_review',
    LISTING_RATING_VID = 'listings-rating_vid',
    LISTING_RATING_REVIEW_VID = 'listings-rating-review_vid',
    // Browser Mockups
    BROWSER_MOCKUP = 'browser-mockup-01',
    BROWSER_MOCKUP_2 = 'browser-mockup-02',

    // Charts & Data
    LISTINGS_CHART_VID = 'listings-chart_vid',
    LISTINGS_SIDE_CHART_VID = 'listings-side-chart_vid',
    FLOWCHART_TXT = 'flowchart_txt',

    // Interactive
    QUIZ = 'quiz',
}

export const TITLES_SCENES = [
    CompositionTypeEnum.PRESENTED_BY,
    CompositionTypeEnum.CTA_TXT,
    CompositionTypeEnum.HEADLINE_TXT,
    CompositionTypeEnum.HEADLINE_AND_SUBTITLE_TXT,
    CompositionTypeEnum.LOGO_INTRO,
    CompositionTypeEnum.QUOTE_TXT,
    CompositionTypeEnum.SUBTITLE_AND_BULLETS_TXT,
];
export const IMAGE_SCENES = [
    CompositionTypeEnum.BROWSER_MOCKUP_2,
    CompositionTypeEnum.IMG_SHADOW,
    CompositionTypeEnum.IMG_AND_HEADLINE_TXT,
    CompositionTypeEnum.IMG_SINGLE,
    CompositionTypeEnum.IMG_SLIDES,
    CompositionTypeEnum.IMG_SINGLE_STATIC,
    CompositionTypeEnum.IMG_SPLIT_2_STATIC,
    CompositionTypeEnum.IMG_SPLIT_3_STATIC,
    CompositionTypeEnum.IMG_SPLIT_4_STATIC,
    CompositionTypeEnum.BROWSER_MOCKUP,
    CompositionTypeEnum.TWO_IMG_SLIDES,
    CompositionTypeEnum.THREE_IMG_SLIDES,
    CompositionTypeEnum.TWO_IMG_FRAMES_TXT,
    CompositionTypeEnum.THREE_IMG_FRAMES_TXT,
    CompositionTypeEnum.FOUR_IMG_FRAMES_TXT,
];
export const VIDEO_AND_TEXT_SCENES = [
    CompositionTypeEnum.VID_AND_HEADLINE_TXT,
    CompositionTypeEnum.VID_AND_NAME_SUPER,
    CompositionTypeEnum.VID_AND_LOGO_BUG,
    CompositionTypeEnum.VID_AND_PIP_CENTER_AND_SUBTITLE_TXT,
];
export const FRAMED_VIDEO_SCENES = [
    CompositionTypeEnum.VID_AND_PIP_CENTER_LARGE,
    CompositionTypeEnum.VID_CLEAN,
    CompositionTypeEnum.VID_SHADOW,
];
export const BULLET_SCENES = [
    CompositionTypeEnum.VID_AND_BULLETED_LIST,
    CompositionTypeEnum.BULLETED_LIST,
    CompositionTypeEnum.FLOWCHART_TXT,
];

export const GENERAL_SCENES: CompositionTypeEnum[] = [];
export const ONLY_AUDIO_COMPOSITIONS: CompositionTypeEnum[] = [
    CompositionTypeEnum.CTA_TXT,
    CompositionTypeEnum.LOGO_INTRO,
    CompositionTypeEnum.IMG_SINGLE,
    CompositionTypeEnum.HEADLINE_AND_SUBTITLE_TXT,
    CompositionTypeEnum.IMG_AND_HEADLINE_TXT,
    CompositionTypeEnum.HEADLINE_TXT,
    CompositionTypeEnum.VID_AND_LEFT_LOGO_BUG_STATIC,
    CompositionTypeEnum.VID_AND_RIGHT_LOGO_BUG_STATIC,
    CompositionTypeEnum.VID_PIP_AND_LEFT_BULLETS_TXT,
    CompositionTypeEnum.VID_PIP_AND_RIGHT_BULLETS_TXT,
    CompositionTypeEnum.VID_PIP_SMALL_AND_LARGE,

    CompositionTypeEnum.PROPERTY_RATINGS,
    CompositionTypeEnum.CUSTOMER_RATINGS,
    CompositionTypeEnum.CUSTOMER_REVIEW,
    CompositionTypeEnum.LISTING_RATING_VID,
    CompositionTypeEnum.LISTING_RATING_REVIEW_VID,
    CompositionTypeEnum.CONTACT_INFO_TXT,
    CompositionTypeEnum.HEADLINE_1MIN_TXT,
    CompositionTypeEnum.NUMBER_AND_SUBTITLE_TXT,
    CompositionTypeEnum.LOGO_END,
    CompositionTypeEnum.VID_PIP_INTERVIEW,
    CompositionTypeEnum.VID_AND_LOWER_BANNER_TXT,
    CompositionTypeEnum.VID_AND_UPPER_BANNER_TXT,
    CompositionTypeEnum.FOUR_IMG_FRAMES_TXT,
    CompositionTypeEnum.THREE_IMG_SLIDES,
    CompositionTypeEnum.BROWSER_MOCKUP,
    CompositionTypeEnum.IMG_SINGLE_STATIC,
];

export interface IComposition {
    _id: string;
    layouts: IExtendedLayout[]; /// might be few layouts per template (currently only 1), meaning few lotties .
    name: string;
    type: CompositionTypeEnum;
    description: string;
    aspectRatio?: string;
    duration?: number;
}

export interface IListRequestOptions {
    limit: number;
    skip: number;
    sort?: string;
    projectId?: string;
}

export enum GenericStatusEnum {
    NONE = 'none',
    INITIAL = 'initial',
    PENDING = 'pending',
    STARTED = 'started',
    FAILED = 'failed',
    DONE = 'done',
}

// LAUNCH DEMO DEFINES

export interface IquestionBank {
    question: string;
    shortQuestion: string;
}

export enum ILaunchStepEnum {
    WELCOME = 'welcome',
    USER_INFO = 'user_info',
    SURPRISE = 'surprise',
    SHOW_QUESTION = 'show_question',
    CHALLENGE = 'challenge',
    FINISH = 'finish',
    BRANDING = 'branding',
    GENERATE_QUESTION = 'generateQuestion',
}

export interface ILaunchFormQuestionConfigs {
    id: string;
    value?: string | number;
}

export interface ILaunchFormQuestion extends ILaunchFormQuestionConfigs {
    type: QuestionTypeEnum;
    options?: string[];
    innerLabel?: string;
    importButton?: boolean;
    text?: string;
}

export interface ILaunchFormStep {
    id?: ILaunchStepEnum;
    stepTitle?: string;
    body?: string;
    stepSubTitle?: string;
    stepDescription?: string;
    midTitle: string;
    videoHLS?: string;
    videoMP4?: string;
    topIcon?: string;
    questionsBank?: IquestionBank[];
    questions?: ILaunchFormQuestion[];
    hideInUI?: boolean;
}

export interface IDefaultLaunchBrand {
    logoUrl: string;
    colors: {
        colorPrimary: string;
        colorSecondary: string;
        colorExtra: string;
    };
    company: {
        id?: string;
        name?: string;
        domain?: string;
    };
}

export interface ILaunchFormConfig {
    steps: ILaunchFormStep[];
    defaultBranding: IDefaultLaunchBrand;
}

export type FileAcceptAttribute =
    | 'audio/*'
    | 'video/*'
    | 'image/*'
    | 'text/*'
    | '.pdf'
    | '.doc'
    | '.docx'
    | '.xls'
    | '.xlsx'
    | '.csv'
    | '.txt'
    | '.rtf'
    | '.html'
    | '.htm'
    | '.json'
    | '.xml'
    | '.zip'
    | '.rar'
    | '.7z'
    | '.tar'
    | '.gz'
    | '.mp3'
    | '.wav'
    | '.ogg'
    | '.mp4'
    | '.mov'
    | '.avi'
    | '.mkv'
    | '.png'
    | '.jpg'
    | '.jpeg'
    | '.gif'
    | '.bmp'
    | '.svg'
    | '.webp'
    | '.ico'
    | '.heic'
    | '.tif'
    | '.tiff'
    | '.webm';
